import { createFileRoute } from '@tanstack/react-router'
import { useState, useMemo } from 'react'
import { useQuery as useConvexQuery, useMutation } from 'convex/react'
import { api } from 'convex/_generated/api'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import type { Id } from 'convex/_generated/dataModel'
import { FlashcardWaveformPlayer } from '@/components/flashcards/FlashcardWaveformPlayer'
import type { FlashcardWithAudio } from '@/hooks/useWaveSurferPlayer'
import { ErrorBoundary } from '@/components/ErrorBoundary'

type AudioReviewSearch = {
  songId?: string
}

export const Route = createFileRoute('/app/audio-review')({
  component: AudioReviewPage,
  validateSearch: (search: Record<string, unknown>): AudioReviewSearch => {
    return {
      songId: typeof search.songId === 'string' ? search.songId : undefined,
    }
  },
})

function AudioReviewPage() {
  const currentUser = useCurrentUser()
  const { songId } = Route.useSearch()

  if (currentUser === undefined) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-sky-blue text-xl font-mono">Loading...</div>
      </div>
    )
  }

  if (currentUser === null) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-trail-tan text-xl font-mono">Please sign in to review audio clips</div>
      </div>
    )
  }

  return (
    <ErrorBoundary>
      <AudioReviewContent userId={currentUser._id} songId={songId} />
    </ErrorBoundary>
  )
}

function AudioReviewContent({ userId, songId }: { userId: Id<'users'>; songId?: string }) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [selectedFlashcard, setSelectedFlashcard] = useState<string | null>(null)
  const [adjustedTimes, setAdjustedTimes] = useState<{ start: number; end: number } | null>(null)

  // Get flashcards that need review using the AssemblyAI query
  const allFlashcardsNeedingReview = useConvexQuery(api.flashcards.getFlashcardsNeedingAudioReview, {
    userId,
  })

  // Filter flashcards by songId if provided
  const flashcardsNeedingReview = useMemo(() => {
    if (!allFlashcardsNeedingReview) return undefined
    if (!songId) return allFlashcardsNeedingReview

    return allFlashcardsNeedingReview.filter(
      (fc: any) => fc.songId === songId
    )
  }, [allFlashcardsNeedingReview, songId])

  const updateFlashcardAudioClip = useMutation(api.assemblyai.updateFlashcardAudioClip)

  const handleIndexChange = (newIndex: number) => {
    setCurrentIndex(newIndex)
  }

  // Transform data for waveform player
  const flashcardsWithAudio: FlashcardWithAudio[] =
    flashcardsNeedingReview?.map((fc: any) => ({
      _id: fc._id,
      frontSpanish: fc.frontSpanish,
      backEnglish: fc.backEnglish,
      audioClip: fc.audioTimestamp
        ? {
            startTime: fc.audioTimestamp.startTime,
            endTime: fc.audioTimestamp.endTime,
            duration: fc.audioTimestamp.duration,
            alignmentConfidence: fc.audioTimestamp.alignmentConfidence,
            needsReview: fc.audioTimestamp.needsReview,
          }
        : undefined,
    })) || []

  const handleAcceptTimestamp = async (flashcardId: string) => {
    const flashcard = flashcardsNeedingReview?.find((fc: any) => fc._id === flashcardId)
    if (!flashcard?.audioTimestamp) return

    try {
      // Mark as reviewed without changing timestamps
      await updateFlashcardAudioClip({
        flashcardId: flashcardId as Id<'flashcards'>,
        audioClip: {
          ...flashcard.audioTimestamp,
          needsReview: false,
        },
      })
    } catch (error) {
      console.error('Failed to accept timestamp:', error)
      alert('Failed to accept timestamp. Please try again.')
    }
  }

  const handleSaveAdjustedTimestamp = async (flashcardId: string) => {
    if (!adjustedTimes) return

    const flashcard = flashcardsNeedingReview?.find((fc: any) => fc._id === flashcardId)
    if (!flashcard?.audioTimestamp) return

    try {
      // Update with adjusted times
      await updateFlashcardAudioClip({
        flashcardId: flashcardId as Id<'flashcards'>,
        audioClip: {
          ...flashcard.audioTimestamp,
          startTime: adjustedTimes.start,
          endTime: adjustedTimes.end,
          duration: adjustedTimes.end - adjustedTimes.start,
          needsReview: false,
        },
      })
      setAdjustedTimes(null)
      setSelectedFlashcard(null)
    } catch (error) {
      console.error('Failed to save adjusted timestamp:', error)
      alert('Failed to save adjusted timestamp. Please try again.')
    }
  }

  if (!flashcardsNeedingReview) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-sky-blue text-xl font-mono">Loading flashcards...</div>
      </div>
    )
  }

  if (flashcardsNeedingReview.length === 0) {
    return (
      <div className="min-h-screen bg-black text-white py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="terminal-border p-8 text-center">
            <h1 className="text-3xl font-bold text-terminal-green mb-4 font-mono">
              [NO AUDIO CLIPS NEED REVIEW]
            </h1>
            <p className="text-trail-tan text-lg font-mono">
              {songId
                ? 'All flashcards for this song have been extracted successfully!'
                : 'All your flashcard audio clips have been extracted successfully!'}
            </p>
            <a
              href="/app/account"
              className="inline-block mt-6 bg-sky-blue text-black font-bold font-mono py-3 px-6
                       hover:bg-blue-400 transition-colors"
            >
              [RETURN TO DASHBOARD]
            </a>
          </div>
        </div>
      </div>
    )
  }

  // Get the current flashcard's song URL for the waveform player
  // Use current index to support multi-song review or single-song filtering
  const audioUrl = flashcardsNeedingReview[currentIndex]?.fullSongUrl || ''

  if (!audioUrl) {
    return (
      <div className="min-h-screen bg-black text-white py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="terminal-border p-8 text-center">
            <h1 className="text-3xl font-bold text-yellow-500 mb-4 font-mono">
              [AUDIO URL NOT AVAILABLE]
            </h1>
            <p className="text-trail-tan text-lg font-mono">
              Cannot load audio for review. Please contact support.
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="relative">
      {/* Waveform Player */}
      <FlashcardWaveformPlayer
        audioUrl={audioUrl}
        flashcards={flashcardsWithAudio}
        initialIndex={currentIndex}
        onIndexChange={handleIndexChange}
      />

      {/* Timestamp Adjustment Overlay */}
      {selectedFlashcard && adjustedTimes && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="terminal-border p-6 max-w-2xl w-full">
            <h2 className="text-2xl font-bold text-terminal-green font-mono mb-6">
              [ADJUST TIMESTAMPS]
            </h2>

            <div className="space-y-4 mb-6">
              <div>
                <label className="text-xs text-trail-tan font-mono uppercase block mb-2">
                  Start Time (seconds)
                </label>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() =>
                      setAdjustedTimes({
                        ...adjustedTimes,
                        start: Math.max(0, adjustedTimes.start - 0.1),
                      })
                    }
                    className="bg-gray-700 text-white font-mono px-4 py-2 hover:bg-gray-600"
                  >
                    -0.1s
                  </button>
                  <input
                    type="number"
                    value={adjustedTimes.start.toFixed(2)}
                    onChange={(e) =>
                      setAdjustedTimes({
                        ...adjustedTimes,
                        start: parseFloat(e.target.value),
                      })
                    }
                    step="0.1"
                    min="0"
                    className="flex-1 bg-black border-2 border-sky-blue text-white font-mono p-2 text-center"
                  />
                  <button
                    onClick={() =>
                      setAdjustedTimes({
                        ...adjustedTimes,
                        start: adjustedTimes.start + 0.1,
                      })
                    }
                    className="bg-gray-700 text-white font-mono px-4 py-2 hover:bg-gray-600"
                  >
                    +0.1s
                  </button>
                </div>
              </div>

              <div>
                <label className="text-xs text-trail-tan font-mono uppercase block mb-2">
                  End Time (seconds)
                </label>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() =>
                      setAdjustedTimes({
                        ...adjustedTimes,
                        end: Math.max(adjustedTimes.start + 0.1, adjustedTimes.end - 0.1),
                      })
                    }
                    className="bg-gray-700 text-white font-mono px-4 py-2 hover:bg-gray-600"
                  >
                    -0.1s
                  </button>
                  <input
                    type="number"
                    value={adjustedTimes.end.toFixed(2)}
                    onChange={(e) =>
                      setAdjustedTimes({
                        ...adjustedTimes,
                        end: parseFloat(e.target.value),
                      })
                    }
                    step="0.1"
                    min={adjustedTimes.start + 0.1}
                    className="flex-1 bg-black border-2 border-sky-blue text-white font-mono p-2 text-center"
                  />
                  <button
                    onClick={() =>
                      setAdjustedTimes({
                        ...adjustedTimes,
                        end: adjustedTimes.end + 0.1,
                      })
                    }
                    className="bg-gray-700 text-white font-mono px-4 py-2 hover:bg-gray-600"
                  >
                    +0.1s
                  </button>
                </div>
              </div>

              <div className="text-sm text-trail-tan font-mono">
                Duration: {(adjustedTimes.end - adjustedTimes.start).toFixed(2)} seconds
              </div>
            </div>

            <div className="flex gap-4">
              <button
                onClick={() => handleSaveAdjustedTimestamp(selectedFlashcard)}
                className="flex-1 bg-terminal-green text-black font-bold font-mono py-3 px-6
                         hover:bg-green-400 transition-colors"
              >
                [SAVE]
              </button>
              <button
                onClick={() => {
                  setAdjustedTimes(null)
                  setSelectedFlashcard(null)
                }}
                className="flex-1 bg-red-600 text-white font-bold font-mono py-3 px-6
                         hover:bg-red-700 transition-colors"
              >
                [CANCEL]
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Review Actions Bar (Fixed at bottom) */}
      <div className="fixed bottom-0 left-0 right-0 bg-black border-t-2 border-sky-blue p-4 z-40">
        <div className="max-w-6xl mx-auto flex gap-4">
          <button
            onClick={() => {
              const currentFlashcard = flashcardsWithAudio[currentIndex]
              if (currentFlashcard?._id) {
                handleAcceptTimestamp(currentFlashcard._id)
              }
            }}
            className="flex-1 bg-terminal-green text-black font-bold font-mono py-3 px-6
                     hover:bg-green-400 transition-colors"
          >
            [ACCEPT TIMESTAMP]
          </button>
          <button
            onClick={() => {
              const currentFlashcard = flashcardsWithAudio[currentIndex]
              if (currentFlashcard?.audioClip) {
                setSelectedFlashcard(currentFlashcard._id)
                setAdjustedTimes({
                  start: currentFlashcard.audioClip.startTime,
                  end: currentFlashcard.audioClip.endTime,
                })
              }
            }}
            className="flex-1 bg-yellow-500 text-black font-bold font-mono py-3 px-6
                     hover:bg-yellow-400 transition-colors"
          >
            [ADJUST TIMESTAMPS]
          </button>
          <button
            onClick={() => {
              // TODO: Implement re-process functionality
              alert('Re-process functionality coming soon!')
            }}
            className="flex-1 bg-hot-pink text-white font-bold font-mono py-3 px-6
                     hover:bg-pink-400 transition-colors"
          >
            [RE-PROCESS WITH AI]
          </button>
        </div>
      </div>

      {/* Instructions */}
      <div className="fixed top-4 right-4 terminal-border p-4 max-w-md bg-black z-40">
        <h3 className="text-lg font-bold text-terminal-green font-mono mb-2">
          [REVIEW GUIDE]
        </h3>
        <ul className="text-trail-tan font-mono text-xs space-y-1">
          <li>• Use waveform to verify audio matches flashcard text</li>
          <li>• Click [ACCEPT] if timestamps are accurate</li>
          <li>• Click [ADJUST] to fine-tune start/end times (±0.1s)</li>
          <li>• Click [RE-PROCESS] to run AI extraction again</li>
          <li>• Use keyboard shortcuts for faster navigation</li>
        </ul>
      </div>
    </div>
  )
}
